import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Company, CompanyService } from './company.service';

/** Font family keys the backend accepts; the CSS families come with the catalog. */
/** The backend's font keys (voucher_html.elements.FONT_FAMILIES); the catalog carries labels and CSS. */
export type FontFamily =
  | 'sans'
  | 'serif'
  | 'mono'
  | 'roboto'
  | 'open_sans'
  | 'lato'
  | 'montserrat'
  | 'poppins'
  | 'playfair'
  | 'lora'
  | 'garamond'
  | 'pacifico'
  | 'dancing'
  | 'lobster'
  | 'caveat';
export type TextAlign = 'left' | 'center' | 'right';
export type VerticalAlign = 'top' | 'middle' | 'bottom';
export type ElementRender = 'text' | 'multiline' | 'qr' | 'image' | 'box' | 'brand';
export type DesignStatus = 'draft' | 'ready';
export type VoucherType = 'money' | 'product';
export type BackgroundKind = 'file' | 'template' | 'none';
export type ImageSource = 'product' | 'library' | 'logo' | 'banner';
export type ImageFit = 'cover' | 'contain';
export type ImageShape = 'rect' | 'circle';
export type { BoxShape } from '../views/voucher-design-editor/shared/shapes';
import type { BoxShape } from '../views/voucher-design-editor/shared/shapes';

export const VOUCHER_TYPE_LABELS: Record<VoucherType, string> = {
  money: 'Wert-Gutschein',
  product: 'Produkt-Gutschein',
};

export interface ElementFont {
  family: FontFamily;
  size: number; // pt
  bold: boolean;
  italic: boolean;
  color: string; // #RRGGBB
  line_height?: number; // multiple of the font size, default 1.2
}

/** One placed element, in PDF points from the page's top-left corner. */
export interface DesignElement {
  id: string;
  kind: string;
  x: number;
  y: number;
  width: number;
  height: number;
  font?: ElementFont | null;
  align: TextAlign;
  valign: VerticalAlign;
  content?: string | null; // text: own words with placeholders like {code} (shared/placeholders.ts)
  wrap?: boolean; // text: several lines, else one line that shrinks to its width
  qr_color?: string | null; // only for the QR code: #RRGGBB
  qr_background?: string | null; // only for the QR code: #RRGGBB, never transparent
  qr_quiet_zone?: boolean; // only for the QR code: a 4-module margin in the background colour
  brand_color?: string | null; // the Schenkwerk logo's colour; null follows the primary colour
  // image kinds (image, company_logo, company_banner, product_image) and boxes
  image_id?: string | null; // a library picture, only for `image`
  fit?: ImageFit;
  focus_x?: number; // 0..1: which part of a cropped picture stays in view
  focus_y?: number;
  shape?: ImageShape | BoxShape; // pictures: rect or circle; boxes: a BoxShape
  opacity?: number; // 0.1 to 1, for kinds with the opacity property
  border_width?: number; // pt
  border_color?: string; // #RRGGBB
  border_radius?: number; // pt
  fill?: string | null; // only for `box`: #RRGGBB or null for none
  group?: string | null; // elements with the same group move and align together
}

export interface DesignLayout {
  version: 2;
  elements: DesignElement[];
}

/** What can be placed (GET /voucher-designs/catalog), `required` resolved per company. */
export interface CatalogEntry {
  kind: string;
  label: string;
  render: ElementRender; // `text` wraps or not by the element's `wrap`
  singleton: boolean;
  required: boolean; // on money designs, for this company's settings
  required_for_product: boolean; // on product designs
  product_only: boolean;
  default_size: { width: number; height: number };
  aspect_ratio: number | null;
  colors?: boolean; // the QR code: colour and background are adjustable
  image_source: ImageSource | null;
  properties: string[]; // fit, focus, shape, border, radius, fill, color, opacity
  min_size?: { width: number; height: number } | null; // smallest box the backend accepts
  artwork?: string; // the brand kind: its SVG, coloured through currentColor
}

/** Where a placeholder's value comes from; the editor groups its menus by it. */
export type PlaceholderSource = 'voucher' | 'company' | 'product';

/** A value a text can carry: `{code}`, `{company_city}` (the catalog's `placeholders`). */
export interface Placeholder {
  key: string;
  label: string;
  source: PlaceholderSource;
  sample: string; // the company's own value where it has one, else a generic sample
  has_value: boolean; // false: the account lacks this value, the text prints nothing for it
  content_max: number | null; // the length to plan the box for (null: short and fixed)
  product_only: boolean;
  required: boolean; // must appear in a text of every money design
  required_for_product: boolean; // ... of every product design
  preset: string; // what "add" places: `{key}`, or `„{personal_message}“`
  wrap: boolean; // the preset wraps onto several lines
  default_size: { width: number; height: number };
}

/** What every design of a type must carry: element kinds and placeholders. */
export interface RequiredThings {
  elements: string[];
  placeholders: string[];
}

export interface PagePreset {
  key: string;
  label: string;
  width_mm: number;
  height_mm: number;
}

export interface CatalogFont {
  family: FontFamily;
  label: string;
  css: string;
}

export interface DesignCatalog {
  elements: CatalogEntry[];
  placeholders: Placeholder[];
  required: Record<VoucherType, RequiredThings>;
  fonts: CatalogFont[];
  qr_min_contrast?: number;
  page_presets?: PagePreset[];
  limits: {
    max_elements: number;
    font_size_min: number;
    font_size_max: number;
    content_max: number; // characters of a text's content, placeholders as written
    line_height_min?: number;
    line_height_max?: number;
    max_images?: number;
    border_width_max?: number;
    border_radius_max?: number;
  };
}

export interface VoucherDesign {
  public_id: string;
  name: string;
  voucher_type: VoucherType;
  background_kind: BackgroundKind;
  background_template: string | null; // artwork of a template design
  background_color: string | null; // page colour of a design without artwork
  template_key: string | null; // the template it started from
  source_kind: 'pdf' | 'svg' | 'image' | null; // uploads only
  source_filename: string | null;
  page_width_pt: number; // printed size
  page_height_pt: number;
  print_scale: number; // printed size / uploaded artwork
  source_width_pt: number; // the artwork as uploaded
  source_height_pt: number;
  min_print_scale: number; // the printed page stays within 5..105 cm per side
  max_print_scale: number;
  layout: DesignLayout;
  status: DesignStatus;
  missing_required: string[];
  missing_branding: string[]; // 'logo' / 'banner' the design shows but the company has not uploaded
  activation_issues: string[]; // the above as sentences, for the warning icon's tooltip
  is_active: boolean;
  warnings: string[];
  preview_url: string | null; // the uploaded artwork as PNG; null without an upload
  thumbnail_url: string; // the demo voucher on the design; needs the bearer token
  created_at: string;
  updated_at: string;
}

/** A bundled template a new design can start from (GET /voucher-designs/templates). */
export interface DesignTemplate {
  key: string;
  label: string;
  description: string;
  voucher_type: VoucherType;
  page_width_pt: number;
  page_height_pt: number;
  needs_branding: boolean;
  available: boolean; // branded templates need a logo and a banner
  preview_url: string;
}

export interface DesignTemplates {
  templates: DesignTemplate[];
  page_presets: PagePreset[];
}

export interface BlankPageRequest {
  width_mm: number;
  height_mm: number;
  landscape: boolean;
  background_color: string; // #RRGGBB
  voucher_type: VoucherType;
  name?: string | null;
}

export interface PreviewOptions {
  productId?: string | null; // preview as a product voucher of this product
  longTexts?: boolean; // fill name, message and product texts to their planned length
}

/** A layout error from the backend: `loc` names the element index. */
export interface LayoutIssue {
  loc: (string | number)[];
  msg: string;
  type: string;
}

@Injectable({ providedIn: 'root' })
export class VoucherDesignService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  private readonly baseUrl = `${environment.apiUrl}/voucher-designs`;

  list(): Observable<{ designs: VoucherDesign[] }> {
    return this.http.get<{ designs: VoucherDesign[] }>(this.baseUrl);
  }

  get(id: string): Observable<VoucherDesign> {
    return this.http.get<VoucherDesign>(`${this.baseUrl}/${id}`);
  }

  catalog(): Observable<DesignCatalog> {
    return this.http.get<DesignCatalog>(`${this.baseUrl}/catalog`);
  }

  /** The conversion runs synchronously on the server; expect a few seconds. */
  upload(file: File, voucherType: VoucherType = 'money', name?: string): Observable<VoucherDesign> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('voucher_type', voucherType);
    if (name) formData.append('name', name);
    return this.http.post<VoucherDesign>(this.baseUrl, formData);
  }

  templates(): Observable<DesignTemplates> {
    return this.http.get<DesignTemplates>(`${this.baseUrl}/templates`);
  }

  /** A ready design from a bundled template. */
  createFromTemplate(
    templateKey: string,
    voucherType: VoucherType,
    name?: string | null,
  ): Observable<VoucherDesign> {
    return this.http.post<VoucherDesign>(`${this.baseUrl}/from-template`, {
      template_key: templateKey,
      voucher_type: voucherType,
      name: name || null,
    });
  }

  /** A draft on a blank page of the given size and colour. */
  createBlank(request: BlankPageRequest): Observable<VoucherDesign> {
    return this.http.post<VoucherDesign>(`${this.baseUrl}/blank`, {
      ...request,
      name: request.name || null,
    });
  }

  /** A copy, optionally as a design of the other voucher type. */
  copy(
    id: string,
    options: { voucher_type?: VoucherType; name?: string } = {},
  ): Observable<VoucherDesign> {
    return this.http.post<VoucherDesign>(`${this.baseUrl}/${id}/duplicate`, options);
  }

  saveLayout(id: string, layout: DesignLayout): Observable<VoucherDesign> {
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/layout`, { layout });
  }

  /** Print bigger or smaller; the backend scales the layout along. */
  setPrintScale(id: string, printScale: number): Observable<VoucherDesign> {
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/print-size`, {
      print_scale: printScale,
    });
  }

  /** Turn a page without artwork; the elements stay where they are. */
  setOrientation(id: string, landscape: boolean): Observable<VoucherDesign> {
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/page/orientation`, {
      landscape,
    });
  }

  rename(id: string, name: string): Observable<VoucherDesign> {
    return this.http.patch<VoucherDesign>(`${this.baseUrl}/${id}`, { name });
  }

  /** The page colour of a design without artwork. */
  setPageColor(id: string, backgroundColor: string): Observable<VoucherDesign> {
    return this.http.patch<VoucherDesign>(`${this.baseUrl}/${id}`, {
      background_color: backgroundColor,
    });
  }

  /** Replace the background with a file; the page takes the file's size. */
  setBackgroundFile(id: string, file: File): Observable<VoucherDesign> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/background/file`, formData);
  }

  /** One of the bundled artworks as the background; the page becomes A4. */
  setBackgroundTemplate(id: string, templateKey: string): Observable<VoucherDesign> {
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/background/template`, {
      template_key: templateKey,
    });
  }

  /** No artwork: a page in one colour, the size stays. */
  removeBackground(id: string, backgroundColor: string): Observable<VoucherDesign> {
    return this.http.put<VoucherDesign>(`${this.baseUrl}/${id}/background/none`, {
      background_color: backgroundColor,
    });
  }

  /** The DEMO1234 voucher on the design, with an unsaved layout when given. */
  previewPdf(id: string, layout?: DesignLayout, options: PreviewOptions = {}): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/${id}/preview`, this.previewBody(layout, options), {
      responseType: 'blob',
    });
  }

  previewPng(id: string, layout?: DesignLayout, options: PreviewOptions = {}): Observable<Blob> {
    return this.http.post(`${this.baseUrl}/${id}/preview`, this.previewBody(layout, options), {
      params: { format: 'png' },
      responseType: 'blob',
    });
  }

  /**
   * Activation puts the design into the company's slot for its voucher type and
   * answers with the company; adopt it so the settings see the change at once.
   */
  activate(id: string): Observable<Company> {
    return this.http
      .post<Company>(`${this.baseUrl}/${id}/activate`, {})
      .pipe(tap((company) => this.companyService.updateCompanyState(company)));
  }

  delete(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.baseUrl}/${id}`);
  }

  /**
   * The absolute URL of a design's background PNG, for `app-authenticated-image`.
   * Only ever a path under our API base; the response is a `preview_url` path, and
   * nothing else is turned into a request.
   */
  previewImageUrl(design: VoucherDesign): string {
    if (!design.preview_url) return '';
    // a replaced background keeps the path: the version makes the canvas refetch
    return `${this.apiPath(design.preview_url)}?v=${encodeURIComponent(simpleHash(design.updated_at))}`;
  }

  /** A template's artwork in the company's colour, for the canvas of a template design;
   * without `expiry` the variant without the "Gültig bis" box, for a design whose
   * texts do not print the date. */
  templateArtworkUrl(artworkStem: string, expiry = true): string {
    return this.templateArtworkUrlByKey(templateKeyForArtwork(artworkStem), expiry);
  }

  /** The same, by template key: the artwork alone, without the voucher's elements. */
  templateArtworkUrlByKey(key: string, expiry = true): string {
    const version = simpleHash(this.brandingVersion());
    const variant = expiry ? '' : '&expiry=false';
    return `${environment.apiUrl}/voucher-designs/templates/${key}/artwork.svg?v=${encodeURIComponent(version)}${variant}`;
  }

  /**
   * The thumbnail of the demo voucher on a design. The query string changes with
   * everything the picture depends on, so a design or branding change refetches it
   * (the server answers from its cache when nothing changed).
   */
  thumbnailUrl(design: VoucherDesign): string {
    const version = `${design.updated_at}|${this.brandingVersion()}`;
    return `${this.apiPath(design.thumbnail_url)}?v=${encodeURIComponent(simpleHash(version))}`;
  }

  /** Everything about the company that changes how its vouchers look. */
  private brandingVersion(): string {
    const company = this.companyService.company();
    return [
      company?.voucher_app_primary_color,
      company?.logo_image_path,
      company?.banner_image_path,
      company?.name,
      company?.website,
      company?.use_billing_as_business,
      company?.business_street,
      company?.business_zip,
      company?.business_city,
      company?.business_country,
      company?.billing_street,
      company?.billing_zip,
      company?.billing_city,
      company?.billing_country,
    ].join('|');
  }

  /** The demo voucher on a template, as this company would print it. */
  templatePreviewUrl(template: DesignTemplate): string {
    return `${this.apiPath(template.preview_url)}?v=${encodeURIComponent(simpleHash(this.brandingVersion()))}`;
  }

  /** The design in the company's slot for a voucher type, from a list of designs. */
  activeDesign(designs: VoucherDesign[], voucherType: VoucherType): VoucherDesign | null {
    const company = this.companyService.company();
    const id =
      voucherType === 'product'
        ? company?.active_product_design_id
        : company?.active_money_design_id;
    return designs.find((d) => d.public_id === id) ?? null;
  }

  private apiPath(path: string): string {
    return `${environment.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
  }

  private previewBody(layout?: DesignLayout, options: PreviewOptions = {}) {
    return {
      layout: layout ?? null,
      product_id: options.productId ?? null,
      long_texts: !!options.longTexts,
    };
  }
}

/** PDF points to centimetres with one decimal and a German comma: "21,0". */
export function ptToCm(pt: number): string {
  return ((pt * 2.54) / 72).toFixed(1).replace('.', ',');
}

/** PDF points to millimetres, rounded, for labels like "210 × 297 mm". */
export function ptToMm(pt: number): number {
  return Math.round((pt * 25.4) / 72);
}

/** A German sentence for an HTTP error from the design endpoints. */
export function designErrorMessage(error: any, fallback: string): string {
  const status = error?.status;
  if (status === 0 || status === 504) {
    return 'Die Umwandlung hat zu lange gedauert. Bitte versuche eine kleinere oder einfachere Datei.';
  }
  if (status === 429) {
    return 'Zu viele Uploads in kurzer Zeit. Bitte in einer Stunde erneut versuchen.';
  }
  const detail = error?.error?.detail;
  if (typeof detail === 'string' && detail) return detail;
  // No sentence from our API (a proxy's answer, a validation list): say what we know.
  if (status === 413) return 'Die Datei ist zu groß für den Upload.';
  if (status === 415) return 'Dieses Dateiformat wird nicht unterstützt.';
  if (status === 503) return 'Gerade ist viel los. Bitte in einer Minute erneut versuchen.';
  return status ? `${fallback} (Fehler ${status})` : fallback;
}

/** A short, stable string for a cache-busting query parameter. */
function simpleHash(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = (hash * 31 + value.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}

/** "Wert-Gutschein" / "Produkt-Gutschein". */
export function voucherTypeLabel(type: VoucherType): string {
  return VOUCHER_TYPE_LABELS[type];
}

/** The other voucher type, for "copy as …". */
export function otherVoucherType(type: VoucherType): VoucherType {
  return type === 'money' ? 'product' : 'money';
}

/** The artwork stems the backend stores on template designs, by template key. */
const ARTWORK_BY_KEY: Record<string, string> = {
  standard: 'voucher-template',
  branded: 'voucher-custom-template',
  product: 'voucher-product-template',
  product_branded: 'voucher-product-custom-template',
  classic: 'voucher-classic',
  landscape: 'voucher-landscape',
  card: 'voucher-card',
  bold: 'voucher-bold',
  product_classic: 'voucher-classic',
  product_landscape: 'voucher-landscape',
};

/** The template key for an artwork stem (the artwork endpoint is keyed by template). */
export function templateKeyForArtwork(stem: string): string {
  return Object.entries(ARTWORK_BY_KEY).find(([, artwork]) => artwork === stem)?.[0] ?? stem;
}

export function artworkLabel(stem: string): string {
  const key = templateKeyForArtwork(stem);
  return (
    {
      standard: 'Standard',
      branded: 'Mit Logo und Banner',
      product: 'Produkt',
      product_branded: 'Produkt mit Logo und Banner',
      classic: 'Klassisch',
      landscape: 'Querformat',
      card: 'Karte',
      bold: 'Vollfarbe',
    }[key] ?? stem
  );
}

/** Why a design cannot be activated right now, or null when it can. */
export function activationBlocker(design: VoucherDesign): string | null {
  if (design.activation_issues?.length) return design.activation_issues.join(' · ');
  if (design.status !== 'ready') return 'Zuerst alle Pflichtelemente platzieren';
  if (design.missing_branding?.length)
    return 'Logo und Banner zuerst unter „Gutschein-App“ hochladen';
  return null;
}

export function canActivateDesign(design: VoucherDesign): boolean {
  return activationBlocker(design) === null;
}
